import { Widget } from '../Widget.js';
import { ItemsCommand } from './ItemsCommand.js';

// core/items/ItemsCmdController.ts
/**
 * Контроллер выполнения команд.
 *
 * Назначение:
 * - Центральная точка маршрутизации вызовов в один из под-контроллеров:
 *   selection / crud / folders / order.
 * - Вызывает одноимённый метод целевого контроллера по имени из `ItemsCommand`.
 *
 * Аргументы:
 * - `args` — массив аргументов целевого метода (передаются «как есть»).
 *   Совместимость: если передан одиночный аргумент (не массив), он будет
 *   обёрнут в массив; `undefined` → вызов без аргументов.
 *
 * Примеры:
 * - `execute(ItemsCommand.select, [ids])`
 * - `execute(ItemsCommand.update, [id, patch])`
 * - `execute(ItemsCommand.createFolder, [title, parentId])`
 */
class ItemsCmdController {
    constructor(ctrls) {
        this.selection = ctrls.selection;
        this.crud = ctrls.crud;
        this.folders = ctrls.folders;
        this.order = ctrls.order;
    }
    /**
     * Универсальный вход: выполнить команду.
     * @param cmd Ключ команды из `ItemsCommand`
     * @param args Аргументы целевого метода: массив, одиночное значение или `undefined`
     * @returns Результат выполнения или ошибка маршрутизации/вызова
     */
    execute(cmd, args) {
        const ctrl = this.pickController(cmd);
        if (!ctrl) {
            throw new ItemsCmdError('unsupported');
        }
        const argv = Array.isArray(args) ? args : (args === undefined ? [] : [args]);
        const method = cmdToMethod(cmd);
        const fn = ctrl[method];
        if (typeof fn !== 'function')
            throw new ItemsCmdError('unsupported');
        try {
            const result = fn.apply(ctrl, argv);
            if (result === undefined)
                // Функция без результата, просто возвращаем true
                return true;
            else
                // Получены какие то данные
                return { command: cmd, data: result };
        }
        catch (e) {
            const name = ctrl?.constructor?.name ?? 'UnknownController';
            console.error(`[ItemsCmdController] command '${name}'.'${method}' failed`, e);
            throw new ItemsCmdError('failed', e?.message);
        }
    }
    /**
     * Выбор под-контроллера, поддерживающего команду.
     * @param cmd Ключ команды из `ItemsCommand`
     */
    pickController(cmd) {
        if (this.selection && supportsCommand(this.selection, cmd))
            return this.selection;
        if (this.crud && supportsCommand(this.crud, cmd))
            return this.crud;
        if (this.folders && supportsCommand(this.folders, cmd))
            return this.folders;
        if (this.order && supportsCommand(this.order, cmd))
            return this.order;
        return null;
    }
}
/**
 * Специализированная ошибка контроллера команд.
 * kind='unsupported' — нет подходящего контроллера/метода
 * kind='failed'      — метод найден, но при выполнении произошла ошибка
 */
class ItemsCmdError extends Error {
    constructor(kind, message) {
        super(message);
        this.kind = kind;
        this.name = 'ItemsCmdError';
    }
}
/** Проверяет, поддерживает ли контроллер указанную команду */
function supportsCommand(ctrl, cmd) {
    const method = cmd;
    return typeof ctrl[method] === "function";
}
/**
 * Type guard: проверяет, что widget имеет items как ItemsCmdController
 */
function hasItemsCmdController(widget) {
    if (!widget || typeof widget !== "object")
        return false;
    const items = widget.items;
    if (!items || typeof items !== "object")
        return false;
    // execute должен быть функцией
    if (typeof items.execute !== "function")
        return false;
    // хотя бы один контроллер должен быть доступен
    if (items.selection || items.crud || items.folders || items.order) {
        return true;
    }
    return false;
}
/**
 * Находит контроллер items и его виджет на ближайшем хосте.
 * @param fromEl исходный элемент (например, внутри разметки)
 * @returns `{ widget, items }` или `null`, если не найдено
 */
function resolveItemsController(fromEl) {
    if (!fromEl)
        return null;
    try {
        const host = fromEl.hasAttribute('data-widget') ? fromEl : fromEl.closest("[data-widget]");
        if (!host)
            return null;
        const widgets = Widget.allOn(host);
        for (const w of widgets) {
            if (hasItemsCmdController(w)) {
                return { widget: w, items: w.items };
            }
        }
    }
    catch (e) {
        console.error('resolveItemsController', e);
    }
    return null;
}
/** Имя метода контроллера по значению enum (работает и для string-, и для numeric-enum). */
function cmdToMethod(cmd) {
    // string-enum → уже строка; numeric-enum → нужна обратная проекция
    return typeof cmd === 'string' ? cmd : ItemsCommand[cmd];
}

export { ItemsCmdController, ItemsCmdError, hasItemsCmdController, resolveItemsController };
